import { z } from 'zod';
import { BattleEventSchema, BattleReplayDataSchema, BattleWinnerSchema } from './combat.js';

// Szerver → Kliens üzenetek
export const WsServerMessageSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('connected'),
    playerId: z.string().uuid(),
    timestamp: z.string().datetime(),
  }),
  z.object({
    type: z.literal('galaxy_update'),
    systemIds: z.array(z.string().uuid()),
    timestamp: z.string().datetime(),
  }),
  z.object({
    type: z.literal('ping'),
    timestamp: z.string().datetime(),
  }),
  z.object({
    type: z.literal('error'),
    message: z.string(),
  }),
  z.object({
    type: z.literal('fleet.departed'),
    fleetId: z.string().uuid(),
    fromSystemId: z.string().uuid(),
    toSystemId: z.string().uuid(),
    arrivesAt: z.string().datetime(),
    timestamp: z.string().datetime(),
  }),
  z.object({
    type: z.literal('fleet.arrived'),
    fleetId: z.string().uuid(),
    systemId: z.string().uuid(),
    timestamp: z.string().datetime(),
  }),
  z.object({
    type: z.literal('survey.started'),
    fleetId: z.string().uuid(),
    systemId: z.string().uuid(),
    completesAt: z.string().datetime(),
    timestamp: z.string().datetime(),
  }),
  z.object({
    type: z.literal('survey.completed'),
    fleetId: z.string().uuid(),
    systemId: z.string().uuid(),
    newDiscoveries: z.array(z.string().uuid()),
    timestamp: z.string().datetime(),
  }),
  z.object({
    type: z.literal('galaxy.discovery.updated'),
    systemId: z.string().uuid(),
    discoveryStatus: z.enum(['unknown', 'detected', 'surveyed', 'colonized']),
    timestamp: z.string().datetime(),
  }),
  z.object({
    // Stellaris fog: sent to the discoverer only, when a ship reveals a system's name / a foreign civilization
    type: z.literal('discovery.revealed'),
    kind: z.enum(['system', 'civilization']),
    systemId: z.string().uuid(),
    systemName: z.string(),
    civilizationId: z.string().uuid().optional(),
    civilizationName: z.string().optional(),
    timestamp: z.string().datetime(),
  }),
  z.object({
    type: z.literal('construction.started'),
    constructionId: z.string().uuid(),
    systemId: z.string().uuid(),
    constructionType: z.enum(['outpost', 'mining_station', 'energy_station', 'logistics_hub']),
    completesAt: z.string().datetime(),
    timestamp: z.string().datetime(),
  }),
  z.object({
    type: z.literal('outpost.completed'),
    systemId: z.string().uuid(),
    civilizationId: z.string().uuid(),
    timestamp: z.string().datetime(),
  }),
  z.object({
    type: z.literal('station.completed'),
    systemId: z.string().uuid(),
    stationId: z.string().uuid(),
    stationType: z.enum(['mining_station', 'energy_station', 'logistics_hub']),
    timestamp: z.string().datetime(),
  }),
  z.object({
    type: z.literal('resources.updated'),
    metal: z.number().int(),
    energy: z.number().int(),
    credits: z.number().int(),
    timestamp: z.string().datetime(),
  }),
  // M5/4: Live battle streaming
  z.object({
    type: z.literal('battle.live.started'),
    battleId: z.string().uuid(),
    replayData: BattleReplayDataSchema,
    attackerCivId: z.string().uuid(),
    defenderCivId: z.string().uuid(),
  }),
  z.object({
    type: z.literal('battle.tick'),
    battleId: z.string().uuid(),
    tick: z.number().int(),
    events: z.array(BattleEventSchema),
  }),
  z.object({
    type: z.literal('battle.live.completed'),
    battleId: z.string().uuid(),
    winner: BattleWinnerSchema,
  }),
  z.object({
    type: z.literal('research.discovery'),
    systemId: z.string().uuid(),
    kind: z.enum(['archive', 'wreck', 'artifact']),
    timestamp: z.string().datetime(),
  }),
]);
export type WsServerMessage = z.infer<typeof WsServerMessageSchema>;

// Kliens → Szerver üzenetek
export const WsClientMessageSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('pong'),
    timestamp: z.string().datetime(),
  }),
  z.object({
    type: z.literal('subscribe_system'),
    systemId: z.string().uuid(),
  }),
]);
export type WsClientMessage = z.infer<typeof WsClientMessageSchema>;
